import React from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { GRID_ACTION_TYPES, GridActionType } from "../../../../assets/types";
import {
  ClaimHelper,
  isGridActionType,
} from "../../../../assets/utils/helpers";
import { Constants } from "../../../../assets/utils/lib/constants";
import { useSnackbar } from "../../../../assets/vfx/snackbar/vfxSnackbarContext";
import { MessageSeverity } from "../../../../assets/utils/lib/messageSeverity";
import { apiErrorMessage } from "../../../../assets/utils/lib/error";
import { Dialog, LoadingIndicator } from "../../../../assets/vfx";
import { OrganizationForm } from "./organizationForm";
 import { NonprofitOrganizationService } from "./organizationService";
import { NonprofitOrganizationView } from "./organizationView";
import { NonprofitOrganizationPage } from "./organizationPage";
import { OrganizationDto } from "../../../../services/dto/nonprofit/organizationDto";
import { useActionFromQuery } from "../../../../hooks/useActionFromQuery";

export const NonprofitOrganizationContainer = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { communityId: routeCommunityId } = useParams<{
    communityId?: string;
  }>();

  const claimCommunityId = !routeCommunityId
    ? ClaimHelper.getCommunityId()
    : undefined;

  const [data, setData] = React.useState<OrganizationDto>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );
  const [showDialog, setShowDialog] = React.useState<boolean>(false);
  const [communityId, setCommunityId] = React.useState<string | undefined>(
    routeCommunityId ?? claimCommunityId
  );

  const nonprofitOrganizationQuery =
    NonprofitOrganizationService.getNonprofitOrganizationQuery(
      data?.id ?? Constants.emptyGuid
    );
  const lookupQuery = NonprofitOrganizationService.getLookups();
  const { mutateAsync: saveOrganization } = NonprofitOrganizationService.save();
  const { mutate: deActivateOrganization } =
    NonprofitOrganizationService.deActivateOrganization();
  const { mutate: deleteMutate } =
    NonprofitOrganizationService.deleteOrganization();

  const isEditMode = !!(data?.id && data.id !== Constants.emptyGuid);
  const { setSearchAction, clearSearchAction } = useActionFromQuery(handleCustomEvent);
  const closeDrawer = (): void => {
      setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  }
  
  const refreshGrid = (): void => {
    isEditMode && nonprofitOrganizationQuery.refetch();
    queryClient.invalidateQueries({
      queryKey: ["nonprofitOrganizationGridQuery"],
    });
    closeDrawer();
  };

  const handleApiResponse = (response: any, successMessage: string): void => {
    const errorMsg = apiErrorMessage(response.errorMessages);
    if (errorMsg) {
      showSnackbar(errorMsg, MessageSeverity.Error);
    } else {
      showSnackbar(successMessage, MessageSeverity.Success);
      refreshGrid();
    }
  };

  const handleSubmit = async (values: OrganizationDto) => {
    values.id = isEditMode ? data!.id : Constants.emptyGuid;
    const message = isEditMode ? "Organization updated" : "Organization saved";
    return await saveOrganization(values, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const handleDelete = (): void => {
    deleteMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) =>
        handleApiResponse(response, "Organization deleted"),
    });
  };

  function handleCustomEvent(actionType: string, dataId: string)
  {
    if (actionType === GRID_ACTION_TYPES.DELETE) {
      setData({ id: dataId } as OrganizationDto);
      setShowDialog(true);
      return;
    }

    if (isGridActionType(actionType)) {
      setDrawerType(actionType);
      setData({ id: dataId } as OrganizationDto);
      setSearchAction(actionType, dataId);
    }
  };

  const handleEdit = (): void => {
    setData(nonprofitOrganizationQuery.data);
    setDrawerType(GRID_ACTION_TYPES.EDIT);
    setSearchAction(GRID_ACTION_TYPES.EDIT, nonprofitOrganizationQuery.data?.id);
  };

  const handleDeactivate = (): void => {
    const message: string = "Organization deactivated";

    deActivateOrganization(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response) => handleApiResponse(response, message),
    });
  };

  const renderDrawerContent = (): React.JSX.Element | null => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW:
        return (
          <OrganizationForm
            show={true}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={communityId}
            disableCommunity={!!routeCommunityId || !!claimCommunityId}
          />
        );

      case GRID_ACTION_TYPES.EDIT:
        return (
          <OrganizationForm
            data={nonprofitOrganizationQuery.data}
            loading={nonprofitOrganizationQuery.isLoading}
            show={!!nonprofitOrganizationQuery.data}
            onHide={closeDrawer}
            onSave={handleSubmit}
            lookups={lookupQuery?.data}
            communityId={communityId}
            disableCommunity={!!routeCommunityId || !!claimCommunityId}
          />
        );

      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <NonprofitOrganizationView
            data={nonprofitOrganizationQuery?.data}
            show={!!nonprofitOrganizationQuery.data}
            onHide={closeDrawer}
            onDelete={handleDelete}
            onEdit={handleEdit}
            lookups={lookupQuery?.data}
            onDeactivate={handleDeactivate}
          />
        );

      default:
        return null;
    }
  };

  if (lookupQuery.isLoading) {
    return <LoadingIndicator isFullPage />;
  }

  return (
    <>
      <NonprofitOrganizationPage
        communityId={routeCommunityId ?? claimCommunityId}
        lookups={lookupQuery?.data}
        hideTitle={!!routeCommunityId}
        setCommunity={setCommunityId}
        onCustomEvent={handleCustomEvent}
      />
      {renderDrawerContent()}
      <Dialog
        contentText={"Are you sure you want to delete?"}
        contentTitle={"Delete"}
        onClose={() => setShowDialog(false)}
        onConfirm={() => {
          setShowDialog(false);
          handleDelete();
        }}
        open={showDialog}
        closeText={"No, Go Back"}
        confirmText={"Yes, Continue"}
      />
    </>
  );
};
